import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Container,
  Typography,
  CircularProgress,
  Paper,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  TextField,
  Button,
  Alert,
  Chip,
  OutlinedInput,
  ListItemText,
  Divider
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

function ExportData() {
  const [metadata, setMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Export configuration state
  const [exportType, setExportType] = useState('requisiciones');
  const [format, setFormat] = useState('excel');
  const [filters, setFilters] = useState({
    empresaIds: [],
    areaIds: [],
    dateFrom: '',
    dateTo: '',
    status: [],
    includeDetails: true,
    includeAdjuntos: true,
    includeNotas: true
  });
  const [options, setOptions] = useState({
    includeFiles: false,
    humanReadable: true,
    includeRelationships: true,
    // PDF-specific options
    pdfDocumentTypes: ['ordenes_compra'],
    pdfOrganizeBy: 'type',
    pdfFilenameFormat: 'doc_numb'
  });

  useEffect(() => {
    loadMetadata();
  }, []);

  const loadMetadata = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/export/metadata');
      setMetadata(response.data);
      setError('');
    } catch (err) {
      setError('Failed to load export metadata. Please ensure you are logged in as SUPERADMIN.');
      console.error('Error loading metadata:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    setError('');
    setSuccess('');

    try {
      let endpoint = `/api/export/${exportType}`;
      let requestBody = {};

      if (exportType === 'generated-pdfs') {
        // PDF export request
        requestBody = {
          document_types: options.pdfDocumentTypes,
          filters: {
            empresa_ids: filters.empresaIds.length > 0 ? filters.empresaIds : null,
            date_from: filters.dateFrom || null,
            date_to: filters.dateTo || null,
            status: filters.status.length > 0 ? filters.status : null
          },
          options: {
            organize_by: options.pdfOrganizeBy,
            filename_format: options.pdfFilenameFormat
          }
        };
      } else if (exportType === 'comprehensive') {
        // Comprehensive export request (full cycles)
        requestBody = {
          format: 'excel', // Always Excel for comprehensive exports
          filters: {
            empresa_ids: filters.empresaIds.length > 0 ? filters.empresaIds : null,
            date_from: filters.dateFrom || null,
            date_to: filters.dateTo || null,
            status: filters.status.length > 0 ? filters.status : null,
            complete_cycles_only: true,
            final_status_only: false,
            include_details: true,
            include_adjuntos: true,
            include_notas: true
          },
          options: {
            include_files: false, // File downloads not yet implemented
            human_readable: options.humanReadable,
            include_relationships: options.includeRelationships,
            group_by_cycle: true
          }
        };
      } else {
        // Standard export request
        requestBody = {
          format: format,
          filters: {
            empresa_ids: filters.empresaIds.length > 0 ? filters.empresaIds : null,
            area_ids: filters.areaIds.length > 0 ? filters.areaIds : null,
            date_from: filters.dateFrom || null,
            date_to: filters.dateTo || null,
            status: filters.status.length > 0 ? filters.status : null,
            include_details: filters.includeDetails,
            include_adjuntos: filters.includeAdjuntos,
            include_notas: filters.includeNotas
          },
          options: {
            include_files: options.includeFiles,
            human_readable: options.humanReadable,
            include_relationships: options.includeRelationships
          }
        };
      }

      const response = await axios.post(endpoint, requestBody, {
        responseType: 'blob'
      });

      // Download file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Get filename from Content-Disposition header or generate default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `export_${exportType}_${new Date().toISOString().split('T')[0]}`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      } else {
        // Determine extension based on format
        if (exportType === 'generated-pdfs' || exportType === 'comprehensive') {
          filename += '.zip';
        } else if (format === 'json') {
          filename += '.json';
        } else if (format === 'csv') {
          filename += '.csv';
        } else if (format === 'excel') {
          filename += '.xlsx';
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSuccess(`Export completed successfully! File: ${filename}`);
    } catch (err) {
      console.error('Export error:', err);
      if (err.response && err.response.status === 403) {
        setError('Access denied. This feature requires SUPERADMIN role.');
      } else if (err.response && err.response.status === 501) {
        setError('This export type is not yet implemented.');
      } else {
        setError('Export failed. Please check your filters and try again.');
      }
    } finally {
      setExporting(false);
    }
  };

  const handleExportTypeChange = (e) => {
    const newType = e.target.value;
    setExportType(newType);

    // Reset format for PDF exports
    if (newType === 'generated-pdfs') {
      setFormat('zip');
    } else if (format === 'zip') {
      setFormat('excel');
    }
  };

  if (loading) {
    return (
      <Container>
        <CircularProgress />
        <Typography>Loading export options...</Typography>
      </Container>
    );
  }

  if (!metadata) {
    return (
      <Container>
        <Alert severity="error">{error || 'Failed to load export metadata.'}</Alert>
      </Container>
    );
  }

  const getStatusOptions = () => {
    if (exportType === 'requisiciones') {
      return metadata.statuses.requisiciones || [];
    } else if (exportType === 'rfqs') {
      return metadata.statuses.rfqs || [];
    } else if (exportType === 'ordenes_compra' || exportType === 'generated-pdfs') {
      return metadata.statuses.ordenes_compra || [];
    } else if (exportType === 'comprehensive') {
      // For comprehensive, filter by cycle lifecycle status (REQ status_ciclo)
      return ['activo', 'cancelado', 'terminada'];
    }
    return [];
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Data Export
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        Sistema para exportar data en varios formatos. Utilice los filtros para limitar la cantidad de datos exportados. Actualmente se tiene un limite de 100 ciclos para el Comprehensive. Puede existir problemas de memoria al exportar, si eso pasa reduzca la información a exportar.
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Export Type Selector */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Export Type
        </Typography>
        <FormControl fullWidth>
          <InputLabel>Select Export Type</InputLabel>
          <Select
            value={exportType}
            label="Select Export Type"
            onChange={handleExportTypeChange}
          >
            <MenuItem value="requisiciones">Requisiciones (Data)</MenuItem>
            <MenuItem value="rfqs">Solicitudes de Cotización (Data)</MenuItem>
            <MenuItem value="ordenes_compra">Órdenes de Compra (Data)</MenuItem>
            <MenuItem value="generated-pdfs">Generated PDFs (OC/RFQ Templates)</MenuItem>
            <MenuItem value="comprehensive">
              Comprehensive (Ciclos completos: REQ→RFQ→OC)
            </MenuItem>
          </Select>
        </FormControl>
      </Paper>

      {/* PDF Export Options */}
      {exportType === 'generated-pdfs' && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            PDF Export Options
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Document Types:
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={options.pdfDocumentTypes.includes('ordenes_compra')}
                    onChange={(e) => {
                      const types = [...options.pdfDocumentTypes];
                      if (e.target.checked) {
                        types.push('ordenes_compra');
                      } else {
                        const index = types.indexOf('ordenes_compra');
                        if (index > -1) types.splice(index, 1);
                      }
                      setOptions({ ...options, pdfDocumentTypes: types });
                    }}
                  />
                }
                label="Órdenes de Compra (OCs)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={options.pdfDocumentTypes.includes('rfqs')}
                    onChange={(e) => {
                      const types = [...options.pdfDocumentTypes];
                      if (e.target.checked) {
                        types.push('rfqs');
                      } else {
                        const index = types.indexOf('rfqs');
                        if (index > -1) types.splice(index, 1);
                      }
                      setOptions({ ...options, pdfDocumentTypes: types });
                    }}
                  />
                }
                label="Solicitudes de Cotización (RFQs)"
              />
            </FormGroup>
          </Box>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Organize PDFs By</InputLabel>
            <Select
              value={options.pdfOrganizeBy}
              label="Organize PDFs By"
              onChange={(e) => setOptions({ ...options, pdfOrganizeBy: e.target.value })}
            >
              <MenuItem value="type">Document Type (OCs/, RFQs/)</MenuItem>
              <MenuItem value="company">Company (Empresa_A/OCs/, Empresa_B/RFQs/)</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel>Filename Format</InputLabel>
            <Select
              value={options.pdfFilenameFormat}
              label="Filename Format"
              onChange={(e) => setOptions({ ...options, pdfFilenameFormat: e.target.value })}
            >
              <MenuItem value="doc_numb">Document Number (OC-00001_ID.pdf)</MenuItem>
              <MenuItem value="descriptive">Descriptive (OC-00001_ProviderName_Date.pdf)</MenuItem>
            </Select>
          </FormControl>
        </Paper>
      )}

      {/* Format Selector (for non-PDF exports) */}
      {exportType !== 'generated-pdfs' && exportType !== 'comprehensive' && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Export Format
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Select Format</InputLabel>
            <Select
              value={format}
              label="Select Format"
              onChange={(e) => setFormat(e.target.value)}
            >
              <MenuItem value="json">JSON</MenuItem>
              <MenuItem value="csv">CSV</MenuItem>
              <MenuItem value="excel">Excel (XLSX)</MenuItem>
            </Select>
          </FormControl>
        </Paper>
      )}

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>

        {/* Company Filter */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Companies</InputLabel>
          <Select
            multiple
            value={filters.empresaIds}
            onChange={(e) => setFilters({ ...filters, empresaIds: e.target.value })}
            input={<OutlinedInput label="Companies" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((id) => {
                  const empresa = metadata.empresas.find((e) => e.id === id);
                  return <Chip key={id} label={empresa?.nombre || id} size="small" />;
                })}
              </Box>
            )}
          >
            {metadata.empresas.map((empresa) => (
              <MenuItem key={empresa.id} value={empresa.id}>
                <Checkbox checked={filters.empresaIds.indexOf(empresa.id) > -1} />
                <ListItemText primary={empresa.nombre} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Date Range */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            label="Date From"
            type="date"
            value={filters.dateFrom}
            onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="Date To"
            type="date"
            value={filters.dateTo}
            onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Box>

        {/* Status Filter */}
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Status</InputLabel>
          <Select
            multiple
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            input={<OutlinedInput label="Status" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip key={value} label={value} size="small" />
                ))}
              </Box>
            )}
          >
            {getStatusOptions().map((status) => (
              <MenuItem key={status} value={status}>
                <Checkbox checked={filters.status.indexOf(status) > -1} />
                <ListItemText primary={status} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Include Options (for non-PDF exports) */}
        {exportType !== 'generated-pdfs' && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Include:
            </Typography>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.includeDetails}
                    onChange={(e) => setFilters({ ...filters, includeDetails: e.target.checked })}
                  />
                }
                label="Articulos del elemento"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.includeAdjuntos}
                    onChange={(e) => setFilters({ ...filters, includeAdjuntos: e.target.checked })}
                  />
                }
                label="Informacion encrustada"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.includeNotas}
                    onChange={(e) => setFilters({ ...filters, includeNotas: e.target.checked })}
                  />
                }
                label="Notas/comentarios"
              />
            </FormGroup>
          </Box>
        )}
      </Paper>

      {/* Options */}
      {exportType !== 'generated-pdfs' && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Export Options
          </Typography>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={options.humanReadable}
                  onChange={(e) => setOptions({ ...options, humanReadable: e.target.checked })}
                />
              }
              label="Formato legible humano (nombres en lugar de IDs)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={options.includeRelationships}
                  onChange={(e) => setOptions({ ...options, includeRelationships: e.target.checked })}
                />
              }
              label="Incluir nombres de relaciones"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={options.includeFiles}
                  onChange={(e) => setOptions({ ...options, includeFiles: e.target.checked })}
                  disabled
                />
              }
              label="Include attached files (creates ZIP) - Coming Soon"
            />
          </FormGroup>
        </Paper>
      )}

      {/* Summary */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
        <Typography variant="h6" gutterBottom>
          Data Rastreada
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip label={`Requisiciones: ${metadata.counts.requisiciones}`} color="primary" variant="outlined" />
          <Chip label={`RFQs: ${metadata.counts.solicitudes_cotizacion}`} color="primary" variant="outlined" />
          <Chip label={`Órdenes de Compra: ${metadata.counts.ordenes_compra}`} color="primary" variant="outlined" />
        </Box>
      </Paper>

      {/* Export Button */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          variant="contained"
          size="large"
          startIcon={exporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
          onClick={handleExport}
          disabled={exporting || (exportType === 'generated-pdfs' && options.pdfDocumentTypes.length === 0)}
          sx={{ minWidth: 200 }}
        >
          {exporting ? 'Exporting...' : 'Export Data'}
        </Button>
      </Box>
    </Container>
  );
}

export default ExportData;
